<section class="representation">
	<lvr-heading
		[showEditButton]="hasRepresentations() && canEditSubstitutesPermission() && !isEditing()"
		(editClicked)="startEdit()"
	>
		@if (hasRepresentations() && !isEditing()) {
			{{ 'specialist.representation.new_representations_title' | transloco }}
		} @else if (hasRepresentations() && isEditing()) {
			{{ 'specialist.representation.edit_representations_title' | transloco }}
		} @else {
			{{ 'specialist.representation.title' | transloco }}
		}
	</lvr-heading>

	<!-- State: no representations available -->
	@if (!isEditing() && !hasRepresentations()) {
		<lvr-simple-panel>
			<div title>
				<span> {{ 'specialist.representation.new_representation' | transloco }} </span>

				@if (canEditSubstitutesPermission()) {
					<a matIconButton [attr.aria-label]="'specialist.representation.add_hint' | transloco">
						<mat-icon (click)="startEdit()" aria-hidden="true">add</mat-icon>
					</a>
				}
			</div>
			<p content>{{ 'specialist.representation.empty_state' | transloco }}</p>
		</lvr-simple-panel>
	}

	<!-- State: representations available -->
	@if (!isEditing() && hasRepresentations()) {
		<lvr-section class="representation-list">
			<div class="representation-table-container readonly">
				<div class="table-wrap">
					<table class="readonly-table">
						<thead>
							<tr>
								<th scope="col">{{ 'specialist.representation.table_representative' | transloco }}</th>
								<th scope="col">{{ 'specialist.representation.table_role' | transloco }}</th>
								<th scope="col">{{ 'specialist.representation.table_specialisation' | transloco }}</th>
								<th scope="col">{{ 'specialist.representation.table_service' | transloco }}</th>
							</tr>
						</thead>

						<tbody>
							@for (rep of representations(); track rep.id) {
								<tr>
									<td>
										<span class="cell-text">
											{{ getRepresentationDisplayName(rep) }}
										</span>
									</td>
									<td>
										<span class="cell-text">{{ rep.role || '-' }}</span>
									</td>
									<td>
										<span class="cell-text">{{ rep.specialisation || '-' }}</span>
									</td>
									<td>
										<span class="cell-text">{{ rep.service || '-' }}</span>
									</td>
								</tr>
							}
						</tbody>
					</table>
				</div>
			</div>
		</lvr-section>
	}

	<!-- State: editing -->
	@if (isEditing()) {
		<lvr-section class="representation-edit">
			<div class="representation-table-container">
				<div class="table-wrap">
					<table mat-table [dataSource]="filteredSortedPossibleRepresentations()" class="mat-elevation-z2">
						<ng-container matColumnDef="selected">
							<th mat-header-cell *matHeaderCellDef>
								<div class="th-filter-inline actions-filter-placeholder"></div>
							</th>

							<td mat-cell *matCellDef="let rep">
								<mat-checkbox
									[checked]="isSelected(rep)"
									(change)="toggleRepresentation(rep, $event.checked)"
									(click)="$event.stopPropagation()"
									[attr.aria-label]="'Select ' + getRepresentationDisplayName(rep)"
								></mat-checkbox>
							</td>
						</ng-container>

						<ng-container matColumnDef="displayName">
							<th mat-header-cell *matHeaderCellDef>
								<div class="th-inline">
									<span class="th-label" [matTooltip]="'specialist.representation.table_representative' | transloco">
										{{ 'specialist.representation.table_representative' | transloco }}
									</span>

									<button
										matIconButton
										class="btn-tertiary"
										[matMenuTriggerFor]="representationSortMenu"
										(click)="setActiveSortField('displayName')"
									>
										<mat-icon>swap_vert</mat-icon>
									</button>
								</div>

								<div class="th-filter-inline">
									<input
										class="th-filter-input"
										(click)="$event.stopPropagation()"
										[value]="filterSearch()['displayName']"
										(input)="setFilterSearch('displayName', $any($event.target).value)"
										[placeholder]="'specialist.representation.table_representative' | transloco"
									/>
								</div>
							</th>

							<td mat-cell *matCellDef="let rep">
								<span class="cell-text">{{ getRepresentationDisplayName(rep) }}</span>
							</td>
						</ng-container>

						<ng-container matColumnDef="role">
							<th mat-header-cell *matHeaderCellDef>
								<div class="th-inline">
									<span class="th-label" [matTooltip]="'specialist.representation.table_role' | transloco">
										{{ 'specialist.representation.table_role' | transloco }}
									</span>

									<button
										matIconButton
										class="btn-tertiary"
										[matMenuTriggerFor]="representationSortMenu"
										(click)="setActiveSortField('role')"
									>
										<mat-icon>swap_vert</mat-icon>
									</button>
								</div>

								<div class="th-filter-inline">
									<input
										class="th-filter-input"
										(click)="$event.stopPropagation()"
										[value]="filterSearch()['role']"
										(input)="setFilterSearch('role', $any($event.target).value)"
										[placeholder]="'specialist.representation.table_role' | transloco"
									/>
								</div>
							</th>

							<td mat-cell *matCellDef="let rep">
								<span class="cell-text">{{ rep.role || '-' }}</span>
							</td>
						</ng-container>

						<ng-container matColumnDef="specialisation">
							<th mat-header-cell *matHeaderCellDef>
								<div class="th-inline">
									<span class="th-label" [matTooltip]="'specialist.representation.table_specialisation' | transloco">
										{{ 'specialist.representation.table_specialisation' | transloco }}
									</span>

									<button
										matIconButton
										class="btn-tertiary"
										[matMenuTriggerFor]="representationSortMenu"
										(click)="setActiveSortField('specialisation')"
									>
										<mat-icon>swap_vert</mat-icon>
									</button>
								</div>

								<div class="th-filter-inline">
									<input
										class="th-filter-input"
										(click)="$event.stopPropagation()"
										[value]="filterSearch()['specialisation']"
										(input)="setFilterSearch('specialisation', $any($event.target).value)"
										[placeholder]="'specialist.representation.table_specialisation' | transloco"
									/>
								</div>
							</th>

							<td mat-cell *matCellDef="let rep">
								<span class="cell-text">{{ rep.specialisation || '-' }}</span>
							</td>
						</ng-container>

						<tr mat-header-row *matHeaderRowDef="displayedColumns"></tr>
						<tr mat-row *matRowDef="let row; columns: displayedColumns"></tr>
					</table>
				</div>
			</div>

			<div class="actions">
				<lvr-default-actions
					(save)="save()"
					(cancelSave)="cancelEdit()"
					[disableSave]="!hasChanges() || !canEditSubstitutesPermission()"
					[showLoading]="loading()"
				></lvr-default-actions>
			</div>
		</lvr-section>

		<mat-menu #representationSortMenu="matMenu">
			<button mat-menu-item class="sort-menu-item" (click)="setSort(activeSortField(), 'asc')">
				<span class="sort-menu-label">{{ sortLabel('asc') | transloco }}</span>
				<mat-icon class="sort-check" [class.is-hidden]="!isSortOptionActive(activeSortField(), 'asc')">
					check
				</mat-icon>
			</button>

			<button mat-menu-item class="sort-menu-item" (click)="setSort(activeSortField(), 'desc')">
				<span class="sort-menu-label">{{ sortLabel('desc') | transloco }}</span>
				<mat-icon class="sort-check" [class.is-hidden]="!isSortOptionActive(activeSortField(), 'desc')">
					check
				</mat-icon>
			</button>
		</mat-menu>
	}
</section>
